import React, {
  useCallback, useEffect, useState,
} from 'react';
import Avatar from '@material-ui/core/Avatar';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from 'Components/UI/Button';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import {
  displayErrorMessage,
  displaySuccess,
} from 'Helpers/FacetNotificationManager';
import { noop } from 'Helpers/PropTypes';
import useResource from 'Hooks/useResource';
import Uploader from 'Components/Files/GenericUploader/Uploader';
import { ClientProfileOverride } from 'Resources/Client';
import fr from 'facet-rest';
import useProfileImage from 'Hooks/useProfileImage';
import { Alert } from '@material-ui/lab';
import useHomeContext from 'Context/HomeContext';
import { dataAttrs, testAttr } from 'Helpers/Testing';
import { SSNInputSmall } from 'Packages/FacetDesign/Components';
import Employment from './Employment';
import Personal from './Personal';
import Contact from './Contact';
import SectionHeader from './SectionHeader';
import Education from './Education';
import Citizenship from './Citizenship';

const PERSONAL_SECTION = 'personal';
const EMPLOYMENT_SECTION = 'employment';
const CONTACT_SECTION = 'contact';
const EDUCATION_SECTION = 'education';
const CITIZENSHIP_SECTION = 'citizen';
const SENSITIVE_INFORMATION_SECTION = 'sensitive';

const styles = theme => ({
  client: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
    marginBottom: theme.spacing(8),
    position: 'relative',
  },
  section: {
    display: 'flex',
    flexFlow: 'column',
    padding: theme.spacing(13, 2, 4),
    margin: theme.spacing(0, 2, 0),
    width: '305px',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    flex: '1',
    '& button': {
      width: '100%',
    },
  },
  addFamilyMember: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'absolute',
    bottom: '0',
    transform: 'translateY(100%)',
    paddingTop: theme.spacing(2),
    '& .fa': {
      background: theme.palette.facet.accentGreen,
      height: '35px',
      width: '35px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      color: theme.palette.common.white,
    },
  },
  profileContainer: {
    color: theme.palette.facet.primaryBlue,
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    position: 'absolute',
    left: '50%',
    top: '-40px',
    transform: 'translateX(-50%)',
    cursor: 'pointer',
    '& span': {
      marginTop: theme.spacing(1),
    },
  },
  profileImage: {
    color: theme.palette.common.white,
    borderRadius: '50%',
    width: '80px',
    height: '80px',
  },
  unsavedProfileImage: {
    cursor: 'default',
  },
  editImage: {
    display: 'flex',
    alignItems: 'center',
  },
  remove: {
    paddingLeft: theme.spacing(0.5),
    color: theme.palette.facet.red,
    cursor: 'pointer',
  },
  upload: {
    cursor: 'pointer',
    paddingRight: theme.spacing(0.5),
    borderRight: '1px solid grey',
  },
  imageActions: {
    marginTop: theme.spacing(1),
  },
  clientActions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionHeader: {
    color: theme.palette.facet.primaryBlue,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: theme.spacing(2),
  },
  sectionButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    flex: '1 0 0',
  },
  grey: {
    color: theme.palette.facet.primaryGray,
  },
  [theme.breakpoints.down('sm')]: {
    root: {
      display: 'block',
    },
    section: {
      marginBottom: theme.spacing(2),
      '& div.square-button .button': {
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(1),
      },
    },
  },
  alert: {
    margin: theme.spacing(1),
    cursor: 'pointer',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

function ClientCard({
  index,
  classes,
  client,
  showSaveButton,
  handleAddClient,
  handleRemoveClient,
  showAddClient,
  onUpdate,
  onSubmit,
  PaperProps,
}) {
  const [section, setSection] = useState(PERSONAL_SECTION);
  const [editProfilePicture, setEditProfilePicture] = useState(false);
  const [saving, setSaving] = useState(false);
  const { resource, setResourceDeprecated } = useResource(client);
  const { homeResource } = useHomeContext();

  const handleUpdate = useCallback((updatedResource) => {
    if (updatedResource) {
      onUpdate();
      setResourceDeprecated(updatedResource);
    }
  }, [onUpdate, setResourceDeprecated]);

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const coll = await resource.submit({
        linkArgs: {
          metaOverrideSet: ClientProfileOverride,
        },
      });
      handleUpdate(coll.resources().head());
      displaySuccess('Saved');
      setSaving(false);
      onSubmit(coll.resources().head());
    } catch (err) {
      setSaving(false);
      if (err.errors) {
        displayErrorMessage(err, err.errors);
      } else {
        displayErrorMessage(err, 'An unknown error occurred');
      }
    }
  };

  function expandSection(sectionToExpand) {
    if (section === sectionToExpand) {
      setSection(PERSONAL_SECTION);
    } else {
      setSection(sectionToExpand);
    }
  }

  const clientSections = (
    <>
      {section === PERSONAL_SECTION || section === CONTACT_SECTION ? (
        <SectionHeader
          onHeaderClick={() => expandSection(CONTACT_SECTION)}
          title="Contact Information"
          iconClassName={classnames({
            'fa fa-plus-circle': section !== CONTACT_SECTION,
            'fa fa-times-circle': section === CONTACT_SECTION,
          })}
        />
      ) : null}
      {section === PERSONAL_SECTION || section === EMPLOYMENT_SECTION ? (
        <SectionHeader
          onHeaderClick={() => expandSection(EMPLOYMENT_SECTION)}
          title="Employment Information"
          iconClassName={classnames({
            'fa fa-plus-circle': section !== EMPLOYMENT_SECTION,
            'fa fa-times-circle': section === EMPLOYMENT_SECTION,
          })}
        />
      ) : null}
      {section === PERSONAL_SECTION || section === CITIZENSHIP_SECTION ? (
        <SectionHeader
          onHeaderClick={() => expandSection(CITIZENSHIP_SECTION)}
          title="Citizenship Information"
          iconClassName={classnames({
            'fa fa-plus-circle': section !== CITIZENSHIP_SECTION,
            'fa fa-times-circle': section === CITIZENSHIP_SECTION,
          })}
        />
      ) : null}
      {section === PERSONAL_SECTION
      || section === SENSITIVE_INFORMATION_SECTION ? (
        <SectionHeader
          onHeaderClick={() => expandSection(SENSITIVE_INFORMATION_SECTION)}
          title="Sensitive Information"
          iconClassName={classnames({
            'fa fa-plus-circle': section !== SENSITIVE_INFORMATION_SECTION,
            'fa fa-times-circle': section === SENSITIVE_INFORMATION_SECTION,
          })}
        />
        ) : null}
    </>
  );

  const dependentSections = (
    <>
      {section === PERSONAL_SECTION || section === EDUCATION_SECTION ? (
        <SectionHeader
          onHeaderClick={() => expandSection(EDUCATION_SECTION)}
          title="Education Information"
          iconClassName={classnames({
            'fa fa-plus-circle': section !== EDUCATION_SECTION,
            'fa fa-times-circle': section === EDUCATION_SECTION,
          })}
        />) : null}
      {section === PERSONAL_SECTION
      || section === SENSITIVE_INFORMATION_SECTION ? (
        <SectionHeader
          onHeaderClick={() => expandSection(SENSITIVE_INFORMATION_SECTION)}
          title="Sensitive Information"
          iconClassName={classnames({
            'fa fa-plus-circle': section !== SENSITIVE_INFORMATION_SECTION,
            'fa fa-times-circle': section === SENSITIVE_INFORMATION_SECTION,
          })}
        />
        ) : null}
    </>
  );

  const { profileImage, fetchProfileImage, clearProfileImage } = useProfileImage({
    avatarLink: resource?.relationship('profileImage')?.link('related'),
    onError: (err) => {
      if (err.status !== 404) {
        if (err.errors) {
          displayErrorMessage(err, err.errors);
        } else {
          displayErrorMessage(err, 'An unknown error occurred');
        }
      }
    },
  });


  function removeProfileImage() {
    resource
      .relationship('profileImage')
      .link('related')
      .Delete()
      .then(() => {
        setEditProfilePicture(false);
        clearProfileImage();
      });
  }

  useEffect(() => {
    if (
      resource.relationship('profileImage')
      && resource.relationship('profileImage').link('related')
    ) {
      clearProfileImage();
    }
    setResourceDeprecated(client);
  }, [client]);

  useEffect(() => {
    fetchProfileImage();
  }, []);

  const profileImageUrl = resource.relationship('profileImage')
    ? resource
      .relationship('profileImage')
      .link('related')
      .attr('url')
    : '';

  const onAddEmail = useCallback(() => {
    const newResource = resource.attr('emailAddresses').create();
    if (resource.id()) {
      newResource.attr('clientId', String(resource.id()));
    }
    resource
      .attr('emailAddresses')
      .resources()
      .pushItem(null, newResource);
    handleUpdate(resource);
  }, [resource, handleUpdate]);

  return (
    <div className={classes.client} {...dataAttrs('client-card', resource, { [testAttr.dataIndex]: index })}>
      <Paper
        elevation={3}
        className={classes.section}
        {...PaperProps}
      >
        {homeResource.withRelationship(
          fr.relationships.onlyLoginEmailAddress.name,
          () => null,
          () => (resource.attr('relationship') === 'spouse'
            || resource.attr('relationship') === 'significantOther') && (
            <Alert
              severity="info"
              className={classes.alert}
              onClick={() => {
                setSection(CONTACT_SECTION);
                onAddEmail();
              }}
            >
              <Typography variant="h6">Want a separate login?</Typography>
              <Typography variant="body">Add a login email address</Typography>
            </Alert>
          ),
        )}
        {resource.id() > 0 ? (
          <Uploader
            accept="image/x-png,image/jpeg"
            url={profileImageUrl}
            onSuccess={() => {
              setEditProfilePicture(false);
              fetchProfileImage();
            }}
            successToastMessage="Successfully saved profile image"
            method="put"
          >
            <div className={classes.profileContainer}>
              <Avatar
                className={classes.profileImage}
                src={profileImage || null}
              />
              <div className={classes.imageActions}>
                {editProfilePicture ? (
                  <div className={classes.editImage}>
                    <Typography className={classes.upload} variant="body1">
                      Upload
                    </Typography>
                    <Typography
                      className={classes.remove}
                      variant="body1"
                      onClick={(e) => {
                        removeProfileImage();
                        e.stopPropagation();
                      }}
                    >
                      Remove
                    </Typography>
                  </div>
                ) : (
                  <>
                    <Typography
                      variant="body1"
                      onClick={(e) => {
                        if (profileImage) {
                          e.stopPropagation();
                          setEditProfilePicture(true);
                        }
                      }}
                    >
                      Change Profile Picture
                    </Typography>
                  </>
                )}
              </div>
              <div className={classes.clientActions}>
                {resource.relationship('firstTimeResetPassword') && (
                  <Typography
                    variant="body1"
                    color="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      const primaryEmailAddress = resource.attr('emailAddresses').resources().list().find(v => v.attr('isPrimary') === 'true');
                      if (primaryEmailAddress) {
                        fr.http.Post(resource.relationship('firstTimeResetPassword').link('related').url(), {
                          attributes: {
                            email: primaryEmailAddress.attr('address'),
                          },
                        }).then(() => {
                          displaySuccess('Login email sent');
                        }).catch((err) => {
                          displayErrorMessage(err, err.errors);
                        });
                      } else {
                        displayErrorMessage({
                          message: 'Please set a login email address in the "Contact Information" section below',
                        }, 'Please set a login email address in the "Contact Information" section below');
                      }
                    }}
                  >
                    Send login info
                  </Typography>
                )}
              </div>
            </div>
          </Uploader>
        ) : (
          <Avatar
            className={classnames({
              [classes.profileContainer]: true,
              [classes.profileImage]: true,
              [classes.unsavedProfileImage]: true,
            })}
            src={profileImage}
          />
        )}
        {section === PERSONAL_SECTION && (
        <Personal
          client={resource}
          handleUpdate={handleUpdate}
        />
        )}
        {resource.attr('relationship') === 'child'
        || resource.attr('relationship') === 'dependent'
          ? dependentSections
          : clientSections}
        {section === CONTACT_SECTION && (
          <Contact
            client={resource}
            handleUpdate={handleUpdate}
            onHeaderClick={() => expandSection(CONTACT_SECTION)}
            onAddEmail={onAddEmail}
          />
        )}
        {section === EMPLOYMENT_SECTION && (
          <Employment client={resource} handleUpdate={handleUpdate} />
        )}
        {section === EDUCATION_SECTION && (
          <Education client={resource} handleUpdate={handleUpdate} />
        )}
        {section === CITIZENSHIP_SECTION && (
          <Citizenship client={resource} handleUpdate={handleUpdate} />
        )}
        {section === SENSITIVE_INFORMATION_SECTION && (
          <SSNInputSmall
            disabled={resource.meta().field('socialSecurityNumber').attr('readonly')}
            label={'Social Security Number'}
            value={resource.attr('socialSecurityNumber')}
            placeholder={resource.meta().field('socialSecurityNumber').attr('placeholder')}
            onChange={(val) => handleUpdate(resource.attr('socialSecurityNumber', val))}
          />
        )}
        {resource.attr('isPrimary') !== 'true' && (
          <SectionHeader
            onHeaderClick={() => handleRemoveClient(resource)}
            title="Remove Family Member"
            iconClassName="fa fa-times-circle"
            color="primaryGray"
          />
        )}
        {showSaveButton && (client.attr('isPrimary') === 'true' || !!resource.attr('relationship')) && (
          <div className={classes.buttonContainer}>
            <Button
              color="secondary"
              disableElevation
              onClick={handleSubmit}
              variant="contained"
              disabled={saving}
              {...dataAttrs('save-client-card')}
            >
              {'Save'}
            </Button>
          </div>
        )}
      </Paper>
      {showAddClient && (
        <div
          {...dataAttrs('add-client-card')}
          className={classes.addFamilyMember}
          onClick={handleAddClient}
          role="presentation"
        >
          <FontAwesome icon="plus" fontSize="24px" />
          Add Family Members
        </div>
      )}
    </div>
  );
}

ClientCard.propTypes = {
  handleRemoveClient: PropTypes.func.isRequired,
  handleAddClient: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
  onSubmit: PropTypes.func,
  client: PropTypes.object.isRequired,
  showSaveButton: PropTypes.bool,
  showAddClient: PropTypes.bool,
  PaperProps: PropTypes.object,
  index: PropTypes.number.isRequired,
};

ClientCard.defaultProps = {
  showAddClient: 'false',
  onUpdate: noop,
  onSubmit: noop,
  showSaveButton: true,
  PaperProps: {},
};

export default withStyles(styles)(ClientCard);
